import { DocumentNode } from 'graphql';
import _ from 'lodash';

import { getEditorLineNumberFromParsedCoordinate } from 'src/app/graph/documentationPage/schemaPage/sdlEditorHelpers';
import {
  Coordinate,
  parseCoordinate,
} from 'src/app/graph/proposalLayout/diffHelpers/diffHelpers';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import type { DefinitionLookup } from 'src/lib/parseGraphqlMergedDefinitions';

export const getDiffItemsByLineNumber = (
  diffItems: GraphQLTypes.MatchingProposalsDrawerFragment['diffs'],
  typeDefs: DocumentNode,
) => {
  return _.orderBy(
    Object.entries(
      diffItems.reduce<
        Record<
          number,
          (GraphQLTypes.MatchingProposalsDrawerFragment['diffs'][number] & {
            linkableCoordinateName: string;
            isDescription: boolean;
          })[]
        >
      >((items, currentItem) => {
        const coordinate = parseCoordinate(currentItem.diffItem);
        const isDescription =
          currentItem.diffItem.__typename.includes('Description');
        const lineNumber = getEditorLineNumberFromParsedCoordinate(
          typeDefs,
          coordinate,
          isDescription,
        );
        if (!lineNumber) return items;

        const linkableCoordinateName = getLinkableCoordinateName(
          currentItem.diffItem,
        );

        return {
          ...items,
          [lineNumber]: (items[lineNumber] ?? []).concat({
            ...currentItem,
            isDescription,
            linkableCoordinateName,
          }),
        };
      }, {}),
    ),
    [([lineNumber]) => parseInt(lineNumber, 10)],
  );
};

export const getOrderedLinkableGutterItems = (
  coordinates: GraphQLTypes.ProposalCheckDetailsDiffDisplayFlatDiffItemFragment[],
  typeDefs: DocumentNode,
) => {
  return _.orderBy(
    coordinates.reduce<
      [
        number,
        {
          hasApprovedProposal: boolean;
          linkableCoordinateName: string;
          isDescription: boolean;
          __typename: string;
        },
      ][]
    >((items, currentItem) => {
      const coordinate = parseCoordinate(currentItem.diffItem);
      const isDescription =
        currentItem.diffItem.__typename.includes('Description');
      const lineNumber = getEditorLineNumberFromParsedCoordinate(
        typeDefs,
        coordinate,
        isDescription,
      );
      if (!lineNumber) return items;

      const linkableCoordinateName = getLinkableCoordinateName(
        currentItem.diffItem,
      );

      const itemOnSameEditorLineIfExists = items.find(
        (item) => item[0] === lineNumber,
      );

      if (!itemOnSameEditorLineIfExists) {
        items.push([
          lineNumber,
          {
            hasApprovedProposal: currentItem.hasApprovedProposal,
            linkableCoordinateName,
            isDescription,
            __typename: currentItem.diffItem.__typename,
          },
        ]);
      } else if (
        itemOnSameEditorLineIfExists[1].hasApprovedProposal &&
        !currentItem.hasApprovedProposal
      ) {
        return items.map((item) =>
          item[0] === lineNumber
            ? ([
                lineNumber,
                {
                  hasApprovedProposal: false,
                  linkableCoordinateName,
                  isDescription: false,
                  __typename: currentItem.diffItem.__typename,
                },
              ] satisfies (typeof items)[number])
            : item,
        );
      }
      return items;
    }, []),
    ([lineNumber]) => lineNumber,
  );
};

export const getLinkableCoordinateName = (
  diffItem: GraphQLTypes.FlatDiffItemFragment,
) => {
  return 'coordinate' in diffItem
    ? diffItem.coordinate
    : 'rootType' in diffItem
      ? `$schema.${diffItem.rootType}`
      : '$schema';
};

export const parseCoordinateFromString = (coordinate: string): Coordinate => {
  if (coordinate.startsWith('$schema')) {
    const [, rootType] = coordinate.split('.');
    return {
      schemaDefinition: true,
      schemaExtension: false,
      field: rootType,
    };
  }
  const [typeAndArg, fieldAndArg = ''] = coordinate.split('.');
  const [type, possibleDirectiveArg = ''] = typeAndArg.split('(');
  const [field, possibleFieldArg = ''] = fieldAndArg.split('(');
  const arg =
    possibleDirectiveArg.replace(':)', '') ||
    possibleFieldArg.replace(':)', '');
  if (!type) {
    throw new Error('invalid coordinate');
  } else if (type.startsWith('@')) {
    return {
      directive: type.substring(1),
      arg: arg || undefined,
    };
  } else {
    return {
      type,
      field: field || undefined,
      arg: arg || undefined,
    };
  }
};

// Get a list of removed items that are tied to distinct coordinates
// This is used to determine if we should show a gutter button for a "Change Description" instance
// if the coordinate the description is on has been fully removed.
export const getFullyRemovedCoordinateNames = (
  coordinates: GraphQLTypes.ProposalCheckDetailsDiffDisplayFlatDiffItemFragment[],
) => {
  return coordinates
    .filter(
      (item) =>
        item.diffItem.__typename === 'FlatDiffRemoveDirective' || // @directive
        item.diffItem.__typename === 'FlatDiffRemoveObject' || // Type
        item.diffItem.__typename === 'FlatDiffRemoveField' || // Type.field
        item.diffItem.__typename === 'FlatDiffRemoveArgument' || // Type.field(arg:)
        item.diffItem.__typename === 'FlatDiffRemoveEnum' || // Enum
        item.diffItem.__typename === 'FlatDiffRemoveEnumValue' || // Enum.VALUE
        item.diffItem.__typename === 'FlatDiffRemoveInput' || // InputObjectType
        item.diffItem.__typename === 'FlatDiffRemoveInterface' || // InterfaceType
        item.diffItem.__typename === 'FlatDiffRemoveScalar' || // ScalarType
        item.diffItem.__typename === 'FlatDiffRemoveUnion' || // UnionType
        item.diffItem.__typename === 'FlatDiffRemoveSchemaDefinition' || // SchemaDefinition
        item.diffItem.__typename === 'FlatDiffRemoveSchemaRootOperation', // SchemaDefinition.rootOperation
    )
    .map((item) => getLinkableCoordinateName(item.diffItem));
};

export const getASTNodeFromCoordinate = (
  coordinate: Coordinate,
  definitions: DefinitionLookup | null,
) => {
  if ('schemaDefinition' in coordinate && 'schemaExtension' in coordinate) {
    return definitions?.schema;
  } else if ('directive' in coordinate) {
    const directiveDefinition = definitions?.directives.get(
      coordinate.directive,
    );
    return directiveDefinition;
  } else if ('type' in coordinate) {
    const typeDefinition = definitions?.types.get(coordinate.type);

    if (
      coordinate.field &&
      typeDefinition &&
      'fields' in typeDefinition &&
      typeDefinition.fields
    ) {
      // TODO(ts5.2+): We should be able to remove the spread here and just use the type directly.
      const fieldDefinition = [...typeDefinition.fields].find(
        (f) => f.name.value === coordinate.field,
      );

      return fieldDefinition;
    } else if (
      coordinate.field &&
      typeDefinition &&
      'values' in typeDefinition &&
      typeDefinition.values
    ) {
      const valueDefinition = [...typeDefinition.values].find(
        (v) => v.name.value === coordinate.field,
      );

      return valueDefinition;
    }

    return typeDefinition;
  } else {
    assertUnreachable(coordinate);
  }
};

export const isRemovedDiffItem = (
  diffItem: GraphQLTypes.FlatDiffItemFragment,
  fullyRemovedCoordinates?: string[],
) => {
  const linkableCoordinateName = getLinkableCoordinateName(diffItem);
  return (
    diffItem.__typename.includes('Remove') ||
    (diffItem.__typename.includes('Change') &&
      fullyRemovedCoordinates?.some(
        (removedCoordinateName) =>
          removedCoordinateName === linkableCoordinateName,
      ))
  );
};

export const isAddedOrChangedDiffItem = (
  diffItem: GraphQLTypes.FlatDiffItemFragment,
  fullyRemovedCoordinates?: string[],
) => {
  const linkableCoordinateName = getLinkableCoordinateName(diffItem);
  return (
    diffItem.__typename.includes('Add') ||
    (diffItem.__typename.includes('Change') &&
      !fullyRemovedCoordinates?.some(
        (removedCoordinateName) =>
          removedCoordinateName === linkableCoordinateName,
      ))
  );
};
